import React from "react";
import { Badge, Overline } from "../../../ForBrands/ui/BrandUI";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { BrandCategoryGrid, EngagementByPlatform, HighlightTile, NilBreakdownTable, PLATFORM_GLYPHS, PositionValueChart } from "../coachPieces";
import { EMPTY_VALUE, PLATFORM_LABELS, changeBadge, fmtCompact, fmtMoneyCompact, plural } from "../coachUtils";
import { fullNameOf, nationalRankLabel } from "../../../ForBrands/brandDealUtils";
import PlayerInsight from "./PlayerInsight";

// NIL KPI card (Figma 02c): a MetricCard with an optional change badge in
// its header row. Phones (2411:1335): the full-width reach tile, 16px
// corners, 14px padding, the 22px value and a 10px sub.
const NilKpiCard = ({ label, badge, value, sub }) => {
  const phone = usePhoneTheme();
  if (phone) {
    return (
      <div className="flex min-w-0 flex-col gap-1 break-words rounded-[16px] border border-bm-mutedbg bg-white p-[14px]">
        <div className="flex min-h-[22px] items-center gap-2">
          <Overline className="min-w-0 flex-1">{label}</Overline>
          {badge && <Badge tone="outline">{badge}</Badge>}
        </div>
        <div className="bm-num-ml text-bm-ink">{value}</div>
        <div className="bm-body-xs text-bm-muted">{sub}</div>
      </div>
    );
  }
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-[12px] border border-bm-border bg-white p-5">
      <div className="flex min-h-[22px] items-center justify-between gap-3">
        <Overline>{label}</Overline>
        {badge && <Badge tone="outline">{badge}</Badge>}
      </div>
      <div className="bm-num-l text-bm-ink">{value}</div>
      <div className="bm-body-s text-bm-muted">{sub}</div>
    </div>
  );
};

// Platform saturation: the platform most of the roster has linked, and the
// one to grow when another trails it. The phone row card draws the glyph at
// 28px on its 56px disc.
const SaturationInsight = ({ saturation, layout }) => {
  if (!saturation) return <HighlightTile layout={layout} eyebrow="Platform saturation" title={EMPTY_VALUE} chip="Appears once players link a social account" />;
  const Glyph = PLATFORM_GLYPHS[saturation.platform];
  const grow = saturation.growPlatform !== saturation.platform && saturation.growPct < saturation.pct;
  return (
    <HighlightTile
      layout={layout}
      eyebrow="Platform saturation"
      logo={Glyph && <Glyph size={layout === "row" ? 28 : 40} className="text-bm-muted" />}
      title={PLATFORM_LABELS[saturation.platform] || saturation.platform}
      value={`${saturation.pct}%`}
      chip={grow ? `${PLATFORM_LABELS[saturation.growPlatform]} ${saturation.growPct}%, grow` : null}
    />
  );
};

// Team · NIL Intelligence (Figma 02c): NIL KPIs, engagement against the
// platform benchmarks beside the brand categories, the average CacheValue
// by position (moved here from Performance, BUILD_SPEC B7), the per-player
// breakdown and four insights. Phones (2411:1193): one column; the reach
// tiles, the charts and the insight row cards stack, and the breakdown
// becomes player cards (NilBreakdownTable switches itself).
const TeamNilTab = ({ players, summary, totals, nil, performance }) => {
  const phone = usePhoneTheme();
  const active = players.filter((p) => p.status === "active");
  const highlights = nil?.highlights || {};
  const { highestNilx, biggestOpportunity, platformSaturation, brandMatchLeader } = highlights;
  const engagementGap = nil?.avgEngagement != null && nil?.benchmark != null ? Math.round((nil.avgEngagement - nil.benchmark) * 10) / 10 : null;
  return (
    <>
      <div className={phone ? "flex flex-col gap-3" : "grid gap-3 md:grid-cols-3"}>
        <NilKpiCard label="NILx pool potential" value={fmtMoneyCompact(summary.nilxPool)} sub={plural(totals.activated ?? 0, "active profile")} />
        <NilKpiCard
          label="Total social reach"
          badge={changeBadge(summary.reachDelta30d, { fmt: fmtCompact, suffix: " in 30d" })}
          value={fmtCompact(nil?.totalReach)}
          sub="across all platforms"
        />
        <NilKpiCard
          label="Avg. engagement rate"
          badge={changeBadge(engagementGap, { suffix: "pp" })}
          value={nil?.avgEngagement != null ? `${nil.avgEngagement}%` : EMPTY_VALUE}
          sub={`vs ${nil?.benchmark ?? EMPTY_VALUE}% benchmark`}
        />
      </div>
      <div className={phone ? "flex flex-col gap-[18px]" : "grid gap-4 lg:grid-cols-2"}>
        <EngagementByPlatform rows={nil?.platformEngagement} />
        <BrandCategoryGrid categories={nil?.brandCategories} computing={nil?.matchesComputing} />
      </div>
      {/* Full width: nothing pairs with it in this tab (half a row would leave the other half empty). */}
      <PositionValueChart team={performance?.positionValues} population={performance?.positionPopulation} />
      <NilBreakdownTable players={active} />
      <div className={phone ? "flex flex-col gap-3" : "grid gap-4 md:grid-cols-2 xl:grid-cols-4"}>
        <PlayerInsight
          eyebrow="Highest NILx"
          player={highestNilx}
          title={fullNameOf(highestNilx)}
          value={fmtMoneyCompact(highestNilx?.nilx)}
          chip={nationalRankLabel(highestNilx?.nationalTopPct, highestNilx?.natlBasis)}
          emptyNote="Appears once players carry a NILx value"
        />
        <PlayerInsight
          eyebrow="Biggest opportunity"
          player={biggestOpportunity}
          title={fullNameOf(biggestOpportunity)}
          value={fmtMoneyCompact(biggestOpportunity?.gap)}
          chip="Gap vs. score"
          emptyNote="No NILx gap on the roster"
        />
        <SaturationInsight saturation={platformSaturation} layout={phone ? "row" : "tile"} />
        <PlayerInsight
          eyebrow="Brand fit leader"
          player={brandMatchLeader}
          title={fullNameOf(brandMatchLeader)}
          value={brandMatchLeader && `${brandMatchLeader.matchScore}%`}
          chip={brandMatchLeader?.categories?.join(" + ")}
          emptyNote="Appears once brand matches are computed"
        />
      </div>
    </>
  );
};

export default TeamNilTab;
